/**
 * Original Tile Preview Component
 * 
 * Shows the original tile that was on the map before building placement.
 * Ported from OriginalTile.mxml
 */

import {
    WOOD, CUTTER, SAWMILL, CUTTER_OLD,
    STONE, QUARRY, STONEMASON, QUARRY_OLD,
    IRON, MINE, FOUNDRY, MINE_OLD,
    FOOD, FARM, MILL, FARM_OLD, LAKE, OPEN, WATER, LAND,
    GOLD, MANOR, BAZAAR, WHARF,
    CASA, CELLARIUM, CACHE,
    WALL, DEEP_WATER, TOWN_HALL,
    BARRACKS, CASTLE, CITY_GUARD_HOUSE, SHIPYARD,
    MOONGLOW, STABLES, TEMPLE, TRAINING_GROUND, WORKSHOP
} from '../core/Constants.js';
import { getTileImageKey } from '../utils/TileImageMap.js';
import { getMilbuildingSprite, MILBUILDINGS_SPRITE_MAP, getResbuildingSprite, RESBUILDINGS_SPRITE_MAP } from '../utils/SpriteSheet.js';

export class OriginalTilePreview {
    constructor(containerElement, gameEngine) {
        this.container = containerElement;
        this.gameEngine = gameEngine;
        this.currentTile = null;
        
        this.render();
    }
    
    /**
     * Update for a specific tile
     */
    update(i, j) {
        this.currentTile = { i, j };
        this.render();
    }
    
    /**
     * Get tile name
     */
    getTileName(tile) {
        if (!tile || tile === 0) return 'Empty';
        
        const names = {
            [TOWN_HALL]: 'Basilica',
            [WALL]: 'City Wall',
            [DEEP_WATER]: 'Deep Water',
            [WATER]: 'Water Spot',
            [WOOD]: 'Wood',
            [CUTTER]: 'Forester\'s Hut',
            [SAWMILL]: 'Sawmill',
            [CUTTER_OLD]: 'Forester\'s Hut (Old)',
            [STONE]: 'Stone',
            [QUARRY]: 'Stone Mine',
            [STONEMASON]: 'Mason\'s Hut',
            [QUARRY_OLD]: 'Stone Mine (Old)',
            [IRON]: 'Iron',
            [MINE]: 'Iron Mine',
            [FOUNDRY]: 'Smelter',
            [MINE_OLD]: 'Iron Mine (Old)',
            [FOOD]: 'Food',
            [FARM]: 'Farm Estate',
            [MILL]: 'Grain Mill',
            [FARM_OLD]: 'Farm Estate (Old)',
            [LAKE]: 'Lake',
            [OPEN]: 'Open',
            [LAND]: 'Land',
            [GOLD]: 'Gold',
            [MANOR]: 'Manor',
            [BAZAAR]: 'Bazaar',
            [WHARF]: 'Wharf',
            [CASA]: 'Casa',
            [CELLARIUM]: 'Cellarium',
            [CACHE]: 'Cache',
            [BARRACKS]: 'Barracks',
            [CASTLE]: 'Castle',
            [CITY_GUARD_HOUSE]: 'Guardhouse',
            [SHIPYARD]: 'Shipyard',
            [MOONGLOW]: 'Sorcerer\'s Tower',
            [STABLES]: 'Stables',
            [TEMPLE]: 'Temple',
            [TRAINING_GROUND]: 'Training Arena',
            [WORKSHOP]: 'Blacksmith',
        };
        
        return names[tile] || `Tile 0x${tile.toString(16)}`;
    }
    
    /**
     * Get building key for image lookup
     */
    getBuildingKey(tile) {
        if (!tile || tile === 0) return null;
        
        const keyMap = {
            [TOWN_HALL]: 'building_townhall',
            [CUTTER]: 'building_forester',
            [CUTTER_OLD]: 'building_forester',
            [SAWMILL]: 'building_sawmill',
            [QUARRY]: 'building_quarry',
            [QUARRY_OLD]: 'building_quarry',
            [STONEMASON]: 'building_stonemason',
            [MINE]: 'building_mine',
            [MINE_OLD]: 'building_mine',
            [FOUNDRY]: 'building_foundry',
            [FARM]: 'building_farm',
            [FARM_OLD]: 'building_farm',
            [MILL]: 'building_mill',
            [MANOR]: 'building_manor',
            [BAZAAR]: 'building_bazaar',
            [WHARF]: 'building_wharf',
            [CASA]: 'building_casa',
            [CELLARIUM]: 'building_cellarium',
            [CACHE]: 'building_cache',
            [BARRACKS]: 'building_barracks',
            [CASTLE]: 'building_castle',
            [CITY_GUARD_HOUSE]: 'building_guard_house',
            [SHIPYARD]: 'building_shipyard',
            [MOONGLOW]: 'building_moonglow',
            [STABLES]: 'building_stable',
            [TEMPLE]: 'building_temple',
            [TRAINING_GROUND]: 'building_training_ground',
            [WORKSHOP]: 'building_workshop',
            [WALL]: 'building_wall',
            [DEEP_WATER]: 'water',
            [WATER]: 'water',
            [WOOD]: 'resource_wood',
            [STONE]: 'resource_stone',
            [IRON]: 'resource_iron',
            [LAKE]: 'resource_lake',
            [FOOD]: 'resource_lake',
            [OPEN]: 'icon_build_slots',
            [LAND]: 'land'
        };
        return keyMap[tile] || null;
    }
    
    /**
     * Get image filename from asset key
     */
    getImageFilename(key) {
        if (!key) return null; // Return null instead of placeholder to prevent image loading
        
        // Don't process water or land keys - they don't have images
        // Return null to prevent any image path from being generated
        if (key === 'water' || key === 'land' || key === null || key === undefined) {
            return null;
        }
        
        // Map asset keys to actual filenames (matching AssetLoader paths)
        const filenameMap = {
            'building_townhall': 'basilica-fix-64',
            'building_forester': 'forester-v6-64',
            'building_sawmill': 'sawmill-64',
            'building_quarry': 'quarry-2-64',
            'building_stonemason': 'stonemasonery-64',
            'building_mine': 'iron-mine-64',
            'building_foundry': 'smelter-64',
            'building_farm': 'farm-v3-64',
            'building_mill': 'grain-mill-64-fix',
            'building_manor': 'villa-64',
            'building_bazaar': 'market-64',
            'building_wharf': 'ports-front-flip-64',
            'building_casa': 'basic_house-64',
            'building_cellarium': 'warehouse-v2-64',
            'building_cache': 'hideaway-64',
            'building_barracks': 'barracks-v2-64',
            'building_castle': 'castle-64-fix',
            'building_guard_house': 'guard\'s_house-v2-64',
            'building_shipyard': 'shipyard-front-flip-64',
            'building_moonglow': 'sorcerer\'s-tower-v2-64',
            'building_stable': 'stable-v2-fix-64',
            'building_temple': 'academy',
            'building_training_ground': 'training_arena-64',
            'building_workshop': 'blacksmith-v2-64',
            'building_wall': 'icon_build_slots', // Wall uses build slots icon from UI folder
            // Note: 'water' is intentionally not mapped - water tiles don't have images
            'resource_wood': 'icon_resource_wood',
            'resource_stone': 'icon_resource_stone',
            'resource_iron': 'icon_resource_iron',
            'resource_lake': 'icon_resource_grain',
            'icon_build_slots': 'icon_build_slots',
            'land': 'land'
        };
        
        // Check if it's a resource icon (should use UI folder)
        if (key.startsWith('resource_') || key.startsWith('icon_resource_') || key.startsWith('icon_')) {
            return key;
        }
        
        return filenameMap[key] || key.replace('building_', '').replace(/_/g, '-') + '-64';
    }
    
    /**
     * Render the component
     */
    render() {
        if (!this.currentTile || !this.gameEngine) {
            this.container.innerHTML = `
                <div class="original-tile">
                    <h4>Original Tile</h4>
                    <p class="no-preview">Hover over a tile to see original</p>
                </div>
            `;
            return;
        }
        
        const { i, j } = this.currentTile;
        const originalTile = this.gameEngine.origMap[i][j];
        const currentTile = this.gameEngine.map[i][j];
        
        const isModified = originalTile !== currentTile;
        
        // Get icons for original and current tiles
        // Skip processing for water and deep water tiles entirely
        const originalImageKey = getTileImageKey(originalTile);
        const originalBuildingKey = this.getBuildingKey(originalTile);
        const originalIsMilbuilding = originalBuildingKey && MILBUILDINGS_SPRITE_MAP[originalBuildingKey];
        const originalIsResbuilding = originalBuildingKey && RESBUILDINGS_SPRITE_MAP[originalBuildingKey];
        
        let originalImagePath = '';
        // If no building key (e.g., water/deep water tiles), don't try to load an image
        // Also skip if imageKey is null (which happens for WATER and DEEP_WATER)
        if (!originalBuildingKey || !originalImageKey) {
            originalImagePath = ''; // No image for water/deep water tiles
        } else if (originalIsMilbuilding) {
            // Use military buildings sprite sheet - will be loaded async
            getMilbuildingSprite(originalBuildingKey).then(dataURL => {
                if (dataURL) {
                    const img = this.container.querySelector('.original-tile-icon[data-building-key="' + originalBuildingKey + '"]');
                    if (img) img.src = dataURL;
                }
            }).catch(err => {
                console.warn('Failed to load sprite from milbuildings.png:', err);
            });
            originalImagePath = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg==';
        } else if (originalIsResbuilding) {
            // Use resource buildings sprite sheet - will be loaded async
            getResbuildingSprite(originalBuildingKey).then(dataURL => {
                if (dataURL) {
                    const img = this.container.querySelector('.original-tile-icon[data-building-key="' + originalBuildingKey + '"]');
                    if (img) img.src = dataURL;
                }
            }).catch(err => {
                console.warn('Failed to load sprite from resbuildings.png:', err);
            });
            originalImagePath = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg==';
        } else if (originalBuildingKey) {
            // Only process if we have a building key
            const originalFilename = this.getImageFilename(originalImageKey || originalBuildingKey);
            // Skip if filename is null (e.g., water/deep water tiles)
            if (originalFilename) {
                // Check if it should use UI folder (resource icons, UI icons like icon_build_slots, or wall)
                const originalIsUI = originalBuildingKey && (
                    originalBuildingKey.startsWith('resource_') || 
                    originalBuildingKey.startsWith('icon_resource_') || 
                    originalBuildingKey.startsWith('icon_') ||
                    originalBuildingKey === 'building_wall'
                );
                originalImagePath = originalIsUI 
                    ? `./assets/images/ui/${originalFilename}.png`
                    : `./assets/images/buildings/${originalFilename}.png`;
            }
        }
        
        const currentImageKey = getTileImageKey(currentTile);
        const currentBuildingKey = this.getBuildingKey(currentTile);
        const currentIsMilbuilding = currentBuildingKey && MILBUILDINGS_SPRITE_MAP[currentBuildingKey];
        const currentIsResbuilding = currentBuildingKey && RESBUILDINGS_SPRITE_MAP[currentBuildingKey];
        
        let currentImagePath = '';
        // If no building key (e.g., water/deep water tiles), don't try to load an image
        // Also skip if imageKey is null (which happens for WATER and DEEP_WATER)
        if (!currentBuildingKey || !currentImageKey) {
            currentImagePath = ''; // No image for water/deep water tiles
        } else if (currentIsMilbuilding) {
            // Use military buildings sprite sheet - will be loaded async
            getMilbuildingSprite(currentBuildingKey).then(dataURL => {
                if (dataURL) {
                    const img = this.container.querySelector('.current-tile-icon[data-building-key="' + currentBuildingKey + '"]');
                    if (img) img.src = dataURL;
                }
            }).catch(err => {
                console.warn('Failed to load sprite from milbuildings.png:', err);
            });
            currentImagePath = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg==';
        } else if (currentIsResbuilding) {
            // Use resource buildings sprite sheet - will be loaded async
            getResbuildingSprite(currentBuildingKey).then(dataURL => {
                if (dataURL) {
                    const img = this.container.querySelector('.current-tile-icon[data-building-key="' + currentBuildingKey + '"]');
                    if (img) img.src = dataURL;
                }
            }).catch(err => {
                console.warn('Failed to load sprite from resbuildings.png:', err);
            });
            currentImagePath = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg==';
        } else if (currentBuildingKey) {
            // Only process if we have a building key
            const currentFilename = this.getImageFilename(currentImageKey || currentBuildingKey);
            // Skip if filename is null (e.g., water/deep water tiles)
            if (currentFilename) {
                // Check if it should use UI folder (resource icons, UI icons like icon_build_slots, or wall)
                const currentIsUI = currentBuildingKey && (
                    currentBuildingKey.startsWith('resource_') || 
                    currentBuildingKey.startsWith('icon_resource_') || 
                    currentBuildingKey.startsWith('icon_') ||
                    currentBuildingKey === 'building_wall'
                );
                currentImagePath = currentIsUI 
                    ? `./assets/images/ui/${currentFilename}.png`
                    : `./assets/images/buildings/${currentFilename}.png`;
            }
        }
        
        this.container.innerHTML = `
            <div class="original-tile">
                <h4>Original Tile</h4>
                <div class="tile-preview-info">
                    <div class="tile-name" style="display: flex; align-items: center; gap: 8px;">
                        ${originalImagePath ? `<img src="${originalImagePath}" 
                             alt="${this.getTileName(originalTile)}" 
                             title="${this.getTileName(originalTile)}"
                             class="building-stats-icon original-tile-icon"
                             data-building-key="${originalBuildingKey || ''}"
                             style="width: 32px; height: 32px; object-fit: contain; flex-shrink: 0;"
                             onerror="this.style.display='none'" />` : ''}
                        <span>
                            ${this.getTileName(originalTile)}
                            ${isModified ? ' <span class="modified">(modified)</span>' : ''}
                        </span>
                    </div>
                    <div class="current-tile" style="display: flex; align-items: center; gap: 8px; margin-top: 8px;">
                        ${currentImagePath ? `<img src="${currentImagePath}" 
                             alt="${this.getTileName(currentTile)}" 
                             title="${this.getTileName(currentTile)}"
                             class="building-stats-icon current-tile-icon"
                             data-building-key="${currentBuildingKey || ''}"
                             style="width: 32px; height: 32px; object-fit: contain; flex-shrink: 0;"
                             onerror="this.style.display='none'" />` : ''}
                        <span>Current: ${this.getTileName(currentTile)}</span>
                    </div>
                </div>
            </div>
        `;
        
        // Load sprite sheet images async if needed (already handled in the path setup above)
        // The async loading is done before the innerHTML is set, so the images will update when loaded
    }
}

